import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { setupPermissionMonitoring } from "../../../services/onlineInterviewService";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { setSubmitBtnLoaderAction } from "../../../store/onboarding/slice";
import { getProfileDetailsThunk, interviewCompleteLaterThunk, myProfileStageSubmit, startInterviewThunk } from "../../../store/onboarding/thunk";
import { TCompletionStatus } from "../../../store/onboarding/types";
import { computeMissingDocs } from "../../../utils/missingDocsHelper";
import { isSuperAdmin } from "../../../utils/roleHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleTick from "../../UI/Icons/CircleTick";
import RightArrow from "../../UI/Icons/RightArrow";
import Warning from "../../UI/Icons/Warning";
import MandatoryDocsAlert from "../../UI/Inputs/MandatoryDocsAlert";
import "./OnlineInterview.scss";

const OnlineInterview: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [showEnterPortal, setShowEnterPortal] = useState<boolean>(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [checkPath, setCheckPath] = useState<boolean>(false);
  const [showSubmitBtnTooltip, setShowSubmitBtnTooltip] = useState<boolean>(false);
  const [pendingSteps, setPendingSteps] = useState<string>('');
  const { isImpersonating, impersonationData } = useImpersonation();
  
  const [showRefreshMessage, setShowRefreshMessage] = useState<boolean>(false);
  const [initialPermissions, setInitialPermissions] = useState<{
    camera: PermissionState | null;
    microphone: PermissionState | null;
  }>({ camera: null, microphone: null });

  const handleMouseEnter = () => setShowSubmitBtnTooltip(true);
  const handleMouseLeave = () => setShowSubmitBtnTooltip(false);

  const inProgress = useSelector((state) => state.auth.inProgress);
  const onboardingInprogress = useSelector((state) => state.onboarding.inProgress)
  const myProfileStageSubmitProgress = useSelector((state) => state.onboarding.myProfileStageSubmitProgress);
  const interviewCompleteLaterInProgress = useSelector((state) => state.onboarding.interviewCompleteLaterInProgress);
  const onboardingStartInterviewInProgress = useSelector((state) => state.onboarding.submitBtnInProgress);
  const viddiStatus = useSelector((state) => state.onboarding.viddiStatus);
  const profileData = useSelector((state) => state.onboarding.personaldetails)
  const currentStage = profileData?.current_stage;
  const stepCompletion: TCompletionStatus | null = useSelector(
    (state) => state.onboarding.completedstatus
  );
  const personaldetails = useSelector(
    (state) => state.onboarding.personaldetails
  );
  const qualifications = useSelector(
    (state) => state.onboarding.qualifications
  );
  const user = useSelector((state) => state.auth.user);
  const isAdmin = isSuperAdmin(user);

  const reviewStatus = profileData?.verification_status;
  const { isSectionReopenedFn, reopenedSections } = useReopenedStages();
  const isOnlineInterviewReopened = isSectionReopenedFn('online_interview');


  useEffect(() => {
    if (location.pathname.includes("candidate")) {
      setCheckPath(true);
    }
  }, [location]);

  const handleShowEnterPortal = async () => {
    dispatch(showTransparentPreloaderAction());
    dispatch(setSubmitBtnLoaderAction(false));
    setIframeUrl(null); // Clear iframe first to prevent re-triggering
    const payload = new FormData();
    payload.append("interview_complete_later", "1");
    await dispatch(interviewCompleteLaterThunk(payload));
    await dispatch(getProfileDetailsThunk());
    setShowEnterPortal(false);
    if(!checkPath) {
      navigate(AUTH_ROUTE.JOBS);
    }
    dispatch(hideTransparentPreloaderAction()); 
  };

  const handleEnterPortal = () => {
    navigate(AUTH_ROUTE.JOBS);
  };

  const getStartButtonLabel = () => {
    if (location.pathname.includes('my-profile')) {
      return viddiStatus?.viddi_status === 2 ? "Continue the Interview" : "Start";
    }
    return "Start Interview";
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  useEffect(() => {
    if (!iframeUrl || iframeUrl.length === 0) {
      document.querySelector(".flk-form")?.classList.add("max-w-960px");
    } else {
      document.querySelector(".flk-form")?.classList.remove("max-w-960px");
    }

    let cleanup: (() => void) | null = null;

    const initializePermissionMonitoring = async () => {
      cleanup = await setupPermissionMonitoring(
        iframeUrl,
        initialPermissions,
        setInitialPermissions,
        setShowRefreshMessage
      );
    };

    initializePermissionMonitoring();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [iframeUrl]);

  const handleStartInterview = async () => {
    dispatch(setSubmitBtnLoaderAction(true));
    
    // Request camera and microphone permissions before starting the interview
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      console.log("Media access granted ✅");
      // Stop tracks immediately (we only needed permission)
      stream.getTracks().forEach((t) => t.stop());
    } catch (err: any) {
      console.warn("User denied media access or error occurred:", err);
      if (err.name === "NotAllowedError") {
        console.log("Permission denied — user must allow in settings.");
      }
    }
    
    const payload = new FormData();
    payload.append("interview_complete_later", "0");
    await dispatch(interviewCompleteLaterThunk(payload));
    await dispatch(getProfileDetailsThunk());
    const interviewUrl = viddiStatus?.viddi_authless_login_url;
    if(interviewUrl !== null) {
      dispatch(setSubmitBtnLoaderAction(false));
      const timestampParam = (interviewUrl.includes('?') ? '&amp;' : '?') + '_t=' + Date.now();
      setIframeUrl(interviewUrl + timestampParam)
    } else {
      dispatch(setSubmitBtnLoaderAction(false));
      await dispatch(startInterviewThunk())
    }
  }

  const handleSubmitStage = async() => {
    dispatch(showTransparentPreloaderAction())
    const payload = new FormData();
    payload.append("stage", "1");
    const myprofileStageResponse = await dispatch(myProfileStageSubmit(payload));
    await dispatch(getProfileDetailsThunk());
    dispatch(hideTransparentPreloaderAction());
    if (isFulfilled(myprofileStageResponse)) {
      if(checkPath) {
        const successMessage = isOnlineInterviewReopened ? 'Stage 1 Resubmitted' : 'Stage 1 Submitted';
        dispatch(showNotificationAction(successMessage));
        setTimeout(() => {
          setIframeUrl(null);
          setShowEnterPortal(false);
          window.location.reload();
        }, NOTIFICATION_TIME_OUT);
      } else {
        setShowEnterPortal(true)  
      }
    }
  }

  const checkAllStageCompleted = () => {
    if (
      stepCompletion &&
      stepCompletion.personal_details === 1 &&
      stepCompletion.qualification === 1 &&
      stepCompletion.jobpreference === 1
    ) {
      return true;
    }
    return false;
  }

  useEffect(() => {
    const handleCheckMandatoryQualificationDocs = () =>
      computeMissingDocs(qualifications, profileData?.working_rights, qualifications?.documents);

    const handleCheckMandatoryPersonalDetailsDocs = () => {
      const isMissing = personaldetails?.resume === null || personaldetails === null;
      return isMissing;
    };

    const missingPersonal = handleCheckMandatoryPersonalDetailsDocs();
    const missingQualification = handleCheckMandatoryQualificationDocs();

    const steps: string[] = [];

    if (missingPersonal) steps.push("Personal Details");
    if (missingQualification.length > 0) steps.push("Qualifications");

    const pendingStepsText = steps.join(", ");

    setPendingSteps(pendingStepsText);
  }, [qualifications, personaldetails, profileData?.working_rights]);

  useEffect(() => {
    const interviewUrl = viddiStatus?.viddi_authless_login_url; 
    if(interviewUrl !== null && !profileData?.interview_complete_later && viddiStatus.viddi_candidate_interview_status === 0 && currentStage === null) {
      dispatch(setSubmitBtnLoaderAction(false));
      const timestampParam = (interviewUrl.includes('?') ? '&amp;' : '?') + '_t=' + Date.now();
      setIframeUrl(interviewUrl + timestampParam)
    }
  }, [dispatch, viddiStatus?.viddi_authless_login_url, profileData?.interview_complete_later, viddiStatus.viddi_candidate_interview_status, currentStage])


  return (
    <>
      { (iframeUrl !== null && !showEnterPortal) && 
        <div className="d-flex flex-column gap-25">
          <div className="d-flex flex-column gap-25 mobile-mt-0">
            <div className="fs-40 fw-400 text-black">Online Interview</div>
            {showRefreshMessage && (
              <div className="bg-orange-light px-15px py-10px radius-8px d-flex gap-6 align-items-center">
                <Warning />
                <div className="d-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center w-100">
                  <div className="fs-14 fw-400 text-black flex-grow-1">
                    Camera or microphone permissions have been updated. Please refresh the page.
                  </div>
                  <CustomButton
                    type="button"
                    variant="primary"
                    onClick={handleRefreshPage}
                    className="btn-theme bg-teal text-black fs-12 px-12px py-6px"
                  >
                    Refresh Page
                  </CustomButton>
                </div>
              </div>
            )}
            <iframe
              src={iframeUrl}
              title="viddi-interview"
              className="viddi-interview"
              style={{ width: "100%", height: "100vh" }}
              id="interviewFrame"
              webkit-playsinline="true"
              webkit-allowfullscreen="true"
              loading="eager"
              allow="camera; microphone; fullscreen; display-capture"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads allow-modals allow-orientation-lock allow-pointer-lock allow-presentation"
            ></iframe>
            <div>After completing the interview, click Submit Stage 1, or choose { location.pathname.includes('my-profile') ? 'Complete Later' : 'Complete Later and Enter Portal' } to skip for now.</div>
            <div className="d-flex flex-column flex-lg-row gap-20">
              <CustomButton
                type="submit"
                variant="primary"
                onClick={handleShowEnterPortal}
                isLoading={interviewCompleteLaterInProgress || onboardingInprogress}
                className="btn-theme bg-off-white text-black"
              >
                { location.pathname.includes('my-profile') ? 'Complete Later' : 'Complete Later and Enter Portal' }
              </CustomButton>
              { ((checkAllStageCompleted() && currentStage === null && reviewStatus !== 'verified') || isOnlineInterviewReopened) && (
                <div 
                  className="position-relative" 
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <CustomButton
                    type="submit"
                    isLoading={myProfileStageSubmitProgress}
                    contentClass="d-flex"
                    variant="primary"
                    disabled={pendingSteps !== ""} // ✅ correct property
                    onClick={handleSubmitStage}
                    className={`mobile-w-100 btn-theme ${pendingSteps === ""
                      ? 'bg-off-white text-black' 
                      : 'bg-grey-light text-grey-medium'
                    }`}
                  >
                    <CircleTick fill={`${pendingSteps === "" 
                      ? '#000000' 
                      : '#aaaaaa'
                    }`} />
                    {isOnlineInterviewReopened ? "Resubmit Stage 1" : "Submit Stage 1"}
                  </CustomButton>
                  { (showSubmitBtnTooltip && pendingSteps !== "") && <MandatoryDocsAlert pendingSteps={pendingSteps} /> }
                </div>
              )}
            </div>
          </div>
        </div>
      }

      {(iframeUrl === null && !showEnterPortal) ? 
        <div className="d-flex flex-column gap-25">
          <div className="d-flex flex-column gap-25 max-w-550px online_interview_container mobile-mt-0">
            <div className="fs-40 fw-400 text-black">Online Interview</div>
            <div className={`d-flex flex-column gap-10 ${currentStage === 1 && !isOnlineInterviewReopened && "d-none"}`}>
              { viddiStatus.viddi_status !== 3 && <div className="fs-16 fw-400 text-black">
                This online interview needs to be completed on Viddi.
              </div> }
              <div className="fs-16 fw-400 text-black">
                Please click  ‘{ location.pathname.includes('my-profile') ? "Start" : "Start Interview" }‘ to begin the screening process.
              </div>
            </div>
            <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6 ${currentStage === 1 && !isOnlineInterviewReopened && "d-none"}`}>
              <Warning />
              <div className="fs-13 fw-400 text-black lh-sm">
                All new candidates must complete the video interview. If you are a current ECE casual with Lloyd Connect and their early childhood recruitment team, please reach out to your consultant to confirm you are able to skip this step.
              </div>
            </div>
            { isImpersonating && 
              <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6 ${currentStage === 1 && "d-none"}`}>
                <Warning />
                <div className="fs-13 fw-400 text-black lh-sm">
                  You are currently impersonating a candidate. You cannot start the interview while in impersonation mode.
                </div>
              </div>
            }


          </div>
          <div className={`d-flex flex-column flex-lg-row gap-20 ${currentStage === 1 && !isOnlineInterviewReopened && "d-none"}`}>
            {
              ((!isImpersonating && !isAdmin) || isOnlineInterviewReopened) && (
              <CustomButton
                type="submit"
                isLoading={onboardingStartInterviewInProgress}
                variant="primary"
                disabled={viddiStatus?.viddi_status === 3 && !isOnlineInterviewReopened}
                onClick={handleStartInterview}
                className={`btn-theme bg-teal text-black`}
              >
                {getStartButtonLabel()}
                <RightArrow color={onboardingStartInterviewInProgress ? "#757575" : "#000000"} />
              </CustomButton>
              )
            }
            
            { !checkPath ? 
              <CustomButton
                type="submit"
                variant="primary"
                onClick={handleShowEnterPortal}
                className="btn-theme bg-off-white text-black"
              >
                Complete Later and Enter Portal
              </CustomButton>
            : "" }
            { ((checkAllStageCompleted() && currentStage === null && reviewStatus !== 'verified') || isOnlineInterviewReopened) && (
              <div 
                className="position-relative" 
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <CustomButton
                  type="submit"
                  isLoading={myProfileStageSubmitProgress}
                  contentClass="d-flex"
                  variant="primary"
                  disabled={pendingSteps !== ""}
                  onClick={handleSubmitStage}
                  className={`mobile-w-100 btn-theme ${pendingSteps === ""
                    ? 'bg-off-white text-black' 
                    : 'bg-grey-light text-grey-medium'
                  }`}
                >
                  <CircleTick fill={`${pendingSteps === "" 
                    ? '#000000' 
                    : '#aaaaaa'
                  }`} />
                  {isOnlineInterviewReopened ? "Resubmit Stage 1" : "Submit Stage 1"}
                </CustomButton>
                { (showSubmitBtnTooltip && pendingSteps !== "") && <MandatoryDocsAlert pendingSteps={pendingSteps} /> }
              </div>
            )}
          </div>

          { (currentStage === 1 && checkPath && !isOnlineInterviewReopened) && 
            <>
              {!viddiStatus?.viddi_mail_status && <div className="fs-16 text-black">This step has not been completed, however you may be eligible to bypass this step if our consultants know you already.</div>}
              <div className="fs-16 text-black">Stage 1: Personal Details & Interview is currently in review by our consultants and you will receive an update within 1 business day.</div>
            </>
          }
        </div>
      : "" }

      {showEnterPortal && (
        <div className="max-w-484px">
          <div className="d-flex flex-column gap-30">
            <div className="fs-30 fw-400 text-black">Stage 1 Submitted</div>
            <div className="fs-16 fw-400 text-black">
              Between each stage, it will take{" "}
              <strong>up to 1 business day</strong> for a manual review to be
              completed by our consultants.
            </div>
            <div className="fs-16 fw-400 text-black">
              These are the following stages to expect after Stage 1 is
              approved.
            </div>
            <div className="d-flex flex-column gap-25">
              <div className="d-flex flex-column gap-8">
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 2
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="fs-16 fw-700 text-black lh-1">
                    Compliance Checks
                  </div>
                  <div className="fs-13 fw-700 text-grey-dark lh-1">40min</div>
                </div>
                <ul className="m-0">
                  <li>Police Check</li>
                  <li>Working With Children</li>
                  <li>Right to Work in Australia</li>
                  <li>Reference Check</li>
                </ul>
              </div>
              <div className="d-flex flex-column gap-8">
                <div className="d-flex">
                  <div className="bg-teal-muted border-1 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                    Stage 3
                  </div>
                </div>
                <div className="d-flex flex-row flex-md-column gap-8 align-items-center align-items-md-start">
                  <div className="fs-16 fw-700 text-black lh-1">
                    Documents &amp; Contract
                  </div>
                  <div className="fs-13 fw-700 text-grey-dark lh-1">30min</div>
                </div>
                <ul className="m-0">
                  <li>TFN Declaration</li>
                  <li>Superannuation</li>
                  <li>Contract</li>
                </ul>
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row gap-20">
              <CustomButton
                isLoading={inProgress}
                type="submit"
                variant="primary"
                className="btn-theme bg-teal text-black"
                onClick={handleEnterPortal}
              >
                Enter Portal <RightArrow color={"black"} />
              </CustomButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnlineInterview;
